<script setup>
import { computed, ref, onMounted } from 'vue';
import axios from '@/plugins/axios';
import { useLayoutComponents } from '@/composables/layout';
import { useToast } from '@/composables/use-toast';
import SelectInput from '@/components/forms/inputs/select-input.vue';
import UsersHeader from '@/pages/admin/AdminHeader.vue';
import StatCard from '@/pages/admin/StatCard.vue';
import LegacyDatasetModal from './components/LegacyDatasetModal.vue';
import { sendLegacyDatasets } from './services/legacyDatasetService';

const { TextInput } = useLayoutComponents();
const { addToast } = useToast();

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const itemsPerPageOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
];

const isLoading = ref(false);

const sortField = ref('updated_at');
const sortOrder = ref('initial');

const datasets = ref([]);

const getDatasets = async (skip = 0, take = 1000) => {
    try {
        isLoading.value = true;
        const response = await axios.get(`/api/datasets/all?skip=${skip}&take=${take}`);
        if(response.status === 200 || response.status === 201) {
            datasets.value = response.data;
            isLoading.value = false;
        }
    } catch {
        isLoading.value = false;
    }
}

const filteredDatasets = computed(() => {
    let filtered = datasets.value;
    
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = datasets.value.filter(dataset => 
            dataset.name.toLowerCase().includes(query) ||
            dataset.description.toLowerCase().includes(query)
        );
    }
    
    if (sortField.value && sortOrder.value !== 'initial') {
        filtered = [...filtered].sort((a, b) => {
            let aValue = a[sortField.value];
            let bValue = b[sortField.value];
            
            if (sortField.value === 'created_at' || sortField.value === 'updated_at') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortOrder.value === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }
            
            if (sortOrder.value === 'asc') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });
    }
    
    return filtered;
});

const totalPages = computed(() => {
    return Math.ceil(filteredDatasets.value.length / itemsPerPage.value);
});

const paginatedDatasets = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = start + itemsPerPage.value;
    return filteredDatasets.value.slice(start, end);
});

const toggleSort = (field) => {
    if (sortField.value === field) {
        if (sortOrder.value === 'initial') {
            sortOrder.value = 'asc';
        } else if (sortOrder.value === 'asc') {
            sortOrder.value = 'desc';
        } else {
            sortOrder.value = 'initial';
        }
    } else {
        sortField.value = field;
        sortOrder.value = 'asc';
    }
    currentPage.value = 1;
}

const getSortIcon = (field) => {
    if (sortField.value !== field) {
        return 'uil-arrows-v';
    }
    
    switch (sortOrder.value) {
        case 'asc':
            return 'uil-arrow-up';
        case 'desc':
            return 'uil-arrow-down';
        default:
            return 'uil-arrows-v';
    }
}

// Format file size
const sizeThresholds = {
    'B': (size) => size < 1024,
    'KB': (size) => size >= 1024 && size < 1024 * 1024,
    'MB': (size) => size >= 1024 * 1024 && size < 1024 * 1024 * 1024,
    'GB': (size) => size >= 1024 * 1024 * 1024 && size < 1024 * 1024 * 1024 * 1024,
    'TB': (size) => size >= 1024 * 1024 * 1024 * 1024,
};

const formatFunctions = {
    'B': (size) => `${size} B`,
    'KB': (size) => `${(size / 1024).toFixed(2)} KB`,
    'MB': (size) => `${(size / (1024 * 1024)).toFixed(2)} MB`,
    'GB': (size) => `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`,
    'TB': (size) => `${(size / (1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`,
};

const formatFileSize = (size) => {
    const unit = Object.keys(sizeThresholds).find(unit => sizeThresholds[unit](size));
    
    return formatFunctions[unit] ? formatFunctions[unit](size) : `${size} B`;
};

// Format date
const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

// Format time
const formatTime = (time) => {
    return new Date(time).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const truncateText = (text, maxLength) => {
    return text?.length > maxLength ? text?.substring(0, maxLength) + '...' : text;
}

const hostedDatasets = computed(() => {
    const totalBytes = datasets.value
        .filter(dataset => dataset.file_name && dataset.file_size)
        .reduce((sum, dataset) => sum + (parseInt(dataset.file_size) || 0), 0);
    return parseFloat((totalBytes / (1024 * 1024 * 1024)).toFixed(2));
});

const externalDatasets = computed(() => {
    const totalBytes = datasets.value
        .filter(dataset => !dataset.file_name && dataset.file_size)
        .reduce((sum, dataset) => sum + (parseInt(dataset.file_size) || 0), 0);
    return parseFloat((totalBytes / (1024 * 1024 * 1024)).toFixed(2));
});

const visiblePages = computed(() => {
    const total = totalPages.value;
    const current = currentPage.value;
    const delta = 2;
    
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    
    const range = [];
    const rangeWithDots = [];
    
    range.push(1);
    
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i);
    }
    
    if (total > 1) {
        range.push(total);
    }
    
    for (let i = 0; i < range.length; i++) {
        if (i > 0) {
            const gap = range[i] - range[i - 1];
            if (gap === 2) {
                rangeWithDots.push(range[i - 1] + 1);
            } else if (gap > 2) {
                rangeWithDots.push('...');
            }
        }
        rangeWithDots.push(range[i]);
    }
    
    return rangeWithDots;
});

const showLegacyDatasetsModal = ref(false);
const loadingLegacyDatasets = ref(false);

let legacyDatasetModalRef = null;

const openLegacyDatasetsModal = () => {
    showLegacyDatasetsModal.value = true;
}

const closeLegacyDatasetsModal = () => {
    showLegacyDatasetsModal.value = false;
}

const handleLegacyDatasetsSelected = async (datasets) => {
    try {
        loadingLegacyDatasets.value = true;
        const response = await sendLegacyDatasets(datasets);
        addToast('Legacy datasets sent successfully', 'success');

        if (response && response.status === 201 && legacyDatasetModalRef) {
            legacyDatasetModalRef.reloadData();
        }
    } catch (error) {
        console.error('Error sending legacy datasets:', error);
        addToast('Failed to send legacy datasets', 'error');
    } finally {
        loadingLegacyDatasets.value = false;
    }
}

onMounted(() => {
    getDatasets();
});

</script>
<template>
    <LegacyDatasetModal ref="legacyDatasetModalRef" :show="showLegacyDatasetsModal" :sending="loadingLegacyDatasets" @close="closeLegacyDatasetsModal" @datasets-selected="handleLegacyDatasetsSelected" />
    <main class="p-5 bg-echoes-background-black">
        <!-- Header Section -->
        <UsersHeader>
            <template #title>Datasets</template>
            <template #description> 
                Manage all datasets on the platform
            </template>
        </UsersHeader>

        <!-- Stats Cards -->
        <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard>
                <template #icon>
                    <i class="mdi mdi-database-outline text-3xl text-sky-500"></i>
                </template>
                <template #title>Total Datasets</template>
                <template #value>{{ datasets.length }}</template>
            </StatCard>
            
            <StatCard>
                <template #icon>
                    <i class="mdi mdi-harddisk text-3xl text-green-500"></i>
                </template>
                <template #title>Storage Distribution</template>
                <template #value>
                    <div class="flex items-center justify-center space-x-6 w-full">
                        <div class="text-start w-full">
                            <div class="text-xl font-semibold">{{ hostedDatasets }} GB</div>
                            <div class="text-xs text-gray-400 uppercase tracking-wide">Hosted</div>
                        </div>
                        <div class="h-12 w-px bg-gray-600 opacity-30"></div>
                        <div class="text-start w-full">
                            <div class="text-xl font-semibold">{{ externalDatasets }} GB</div>
                            <div class="text-xs text-gray-400 uppercase tracking-wide">External</div>
                        </div>
                    </div>
                </template>
            </StatCard>
        </div>

        <!-- Main Content -->
        <div class="py-6">
            <div class="bg-echoes-background-gray shadow-sm rounded-xl border border-echoes-border-light">
                <!-- Search and Filters -->
                <div class="p-4">
                    <div class="flex flex-col sm:flex-row gap-4 items-center justify-between w-full">
                        <div class="w-full sm:w-96">
                            <TextInput 
                                v-model="searchQuery" 
                                placeholder="Search by name or description" 
                                class="w-full"
                            />
                        </div>
        
                        <div class="flex items-center gap-2">
                            <button @click="openLegacyDatasetsModal" class="h-10 rounded-xl px-4 text-sm font-medium text-white bg-echoes-primary hover:bg-echoes-primary/80 disabled:opacity-50 disabled:cursor-not-allowed">
                                Legacy Datasets
                            </button>
                            <div class="w-full sm:w-24">
                                <SelectInput
                                    v-model="itemsPerPage"
                                    :options="itemsPerPageOptions"
                                    :hide-search="true"
                                    @update:modelValue="currentPage = 1"
                                />

                            </div>
                        </div>
                    </div>
                </div>

                <!-- Table -->
                <div>
                  
                    <table class="min-w-full divide-y divide-echoes-border-light">
                        <thead class="bg-echoes-border-light ">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                    Dataset Details
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                File Details
                                </th>
                                <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                                Storage
                                </th>
                                <th scope="col" class="px-6 py-3 text-start text-xs font-medium text-white uppercase tracking-wider">
                                Owner
                                </th>
                               <th scope="col" class="px-6 py-3 text-start text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-echoes-background-black/20 transition-colors duration-200" @click="toggleSort('created_at')">
                                Created At 
                                <i :class="`uil text-base ${getSortIcon('created_at')} ml-1`"></i>
                               </th>
                               <th scope="col" class="px-6 py-3 text-start text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-echoes-background-black/20 transition-colors duration-200" @click="toggleSort('updated_at')">
                                Updated At 
                                <i :class="`uil text-base ${getSortIcon('updated_at')} ml-1`"></i>
                               </th>
                               <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                                Actions
                               </th>
                            </tr>
                        </thead>
                        <tbody class="bg-echoes-background-gray divide-y divide-echoes-border-light">
                            <template v-if="isLoading">
                                <tr v-for="i in 4" :key="i" class="bg-echoes-background-black/80 animate-pulse">
                                    <td class="px-2 py-4">
                                        <div class="flex items-center">
                                            <div class="ml-4">
                                                <div class="h-4 bg-echoes-background-gray rounded-full w-32 mb-2"></div>
                                                <div class="h-3 bg-echoes-background-gray rounded-full w-24"></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-2 py-4">
                                        <div class="flex items-center">
                                            <div class="ml-4">
                                                <div class="h-4 bg-echoes-background-gray rounded-full w-32 mb-2"></div>
                                                <div class="h-3 bg-echoes-background-gray rounded-full w-24"></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center">
                                            <div class="h-6 bg-echoes-background-gray rounded-full w-20"></div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <div class="h-10 w-10 rounded-full bg-echoes-background-gray"></div>
                                            </div>
                                            <div class="ml-4">
                                                <div class="h-4 bg-echoes-background-gray rounded-full w-24 mb-2"></div>
                                                <div class="h-3 bg-echoes-background-gray rounded-full w-20"></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center">
                                            <div class="h-6 bg-echoes-background-gray rounded-full w-20"></div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center">
                                            <div class="h-6 bg-echoes-background-gray rounded-full w-20"></div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center">
                                            <div class="h-6 bg-echoes-background-gray rounded-full w-20"></div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <template v-else-if="!isLoading && datasets.length === 0">
                                <tr>
                                    <td colspan="7" class="h-[40vh]">
                                        <div class="flex items-center justify-center h-full w-full">
                                            <div class="text-white/80 text-sm">No datasets found</div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <template v-for="dataset in paginatedDatasets" :key="dataset.id" v-else>
                                <tr class="bg-echoes-background-black/80 hover:bg-echoes-background-black/50 transition-colors duration-200">
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                         
                                            <div>
                                                <div class="text-sm font-medium text-white">
                                                    {{ truncateText(dataset.name, 32) }}
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ truncateText(dataset.description, 32) }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                        
                                            <div>
                                                <div class="text-sm font-medium text-white" :title="dataset.file_name ? dataset.file_name : 'External Dataset'">
                                                    {{ truncateText(dataset.file_name, 25) || 'External File' }}
                                                </div>
                                                <div v-if="dataset.file_size > 0" class="text-sm text-white/80">
                                                    {{ formatFileSize(dataset.file_size) }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center">
                                            <span 
                                                :class="[
                                                    'px-3 py-1 text-xs font-medium rounded-full',
                                                    dataset.file_name 
                                                        ? 'bg-green-500/20 text-green-500'
                                                        : 'bg-blue-500/20 text-blue-500'
                                                ]"
                                            >
                                                {{ dataset.file_name ? 'Hosted' : 'External' }}
                                            </span>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center">
                                            <div class="flex-shrink-0 h-10 w-10">
                                                <div class="h-10 w-10 rounded-full bg-echoes-primary flex items-center justify-center">
                                                    <span class="text-lg font-medium text-white">
                                                        <img :src="dataset.seller.user.profile_pic ? dataset.seller.user.profile_pic : dataset.seller.user.username?.charAt(0)?.toUpperCase() || ''" alt="user-avatar" class="h-10 w-10 rounded-full">
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="ml-4">
                                                <div class="text-sm font-medium text-white">
                                                    {{ dataset.seller.user.username }}
                                                </div>
                                                <div class="text-sm text-white/80">
                                                    {{ dataset.seller.user.user_type ? dataset.seller.user.user_type : '' }}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex flex-col justify-center">
                                            <div class="text-sm font-medium text-white">
                                                {{ formatDate(dataset.created_at) }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                at {{ formatTime(dataset.created_at) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex flex-col justify-center">
                                            <div class="text-sm font-medium text-white">
                                                {{ formatDate(dataset.updated_at) }}
                                            </div>
                                            <div class="text-sm text-white/80">
                                                at {{ formatTime(dataset.updated_at) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="flex items-center justify-center">
                                            <router-link :to="'/dataset-edit/' + dataset.id" target="_blank" 
                                        class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light">
                                        <i class="mdi mdi-pencil mr-2"></i>
                                        Edit
                                    </router-link>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                    <!-- Pagination Footer -->
                    <div class="bg-echoes-background-gray border-t rounded-b-lg border-echoes-border-light px-6 py-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center text-sm text-white/80">
                                <span>Showing</span>
                                <span class="font-medium mx-1">
                                    {{ ((currentPage - 1) * itemsPerPage) + 1 }}
                                </span>
                                <span>to</span>
                                <span class="font-medium mx-1">
                                    {{ Math.min(currentPage * itemsPerPage, filteredDatasets.length) }}
                                </span>
                                <span>of</span>
                                <span class="font-medium ml-1">{{ filteredDatasets.length }}</span>
                            </div>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="currentPage = Math.max(1, currentPage - 1)"
                                    :disabled="currentPage === 1"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <div class="flex items-center space-x-1">
                                    <template v-for="(item, index) in visiblePages" :key="index">
                                        <template v-if="typeof item === 'number'">
                                            <button
                                                @click="currentPage = item"
                                                :class="[
                                                    'px-3 py-1 rounded-md text-sm font-medium',
                                                    currentPage === item
                                                        ? 'bg-echoes-primary text-white'
                                                        : 'text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80'
                                                ]"
                                            >
                                                {{ item }}
                                            </button>
                                        </template>
                                        <template v-else>
                                            <span class="px-3 py-1 text-sm text-white/60">...</span>
                                        </template>
                                    </template>
                                </div>
                                <button
                                    @click="currentPage = Math.min(totalPages, currentPage + 1)"
                                    :disabled="currentPage === totalPages"
                                    class="px-3 py-1 rounded-lg text-sm font-medium text-white/80 bg-echoes-background-black hover:bg-echoes-background-black/80 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>



<style scoped lang="postcss">


.table-container table {
    @apply w-full table-fixed;
    display: table;
}

.table-container thead {
    @apply sticky top-0 z-10;
    background-color: var(--echoes-background-gray);
    display: table;
    width: 100%;
}

.table-container tbody {
    @apply relative;
    height: calc(100vh - 620px);
    overflow-y: auto;
    overflow-x: hidden;
    display: block;
    width: 100%;
}

.table-container tbody tr {
    display: table;
    width: 100%;
    table-layout: fixed;
}



/* Custom Scrollbar */
.table-container tbody::-webkit-scrollbar {
    @apply w-2;
}

.table-container tbody::-webkit-scrollbar-track {
    @apply bg-echoes-background-black rounded-full;
}

.table-container tbody::-webkit-scrollbar-thumb {
    @apply bg-echoes-background-gray rounded-full transition-colors duration-200;
}

.table-container tbody::-webkit-scrollbar-thumb:hover {
    @apply bg-echoes-background-gray/80;
}

/* Animations */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideIn {
    from { transform: translateY(-10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.table-container tr {
    animation: fadeIn 0.3s ease-in-out;
}

/* Stats Cards Hover Effect */
.bg-white.dark\:bg-slate-700 {
    @apply transition-all duration-200 hover:shadow-lg hover:scale-[1.02];
}
</style> 